import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, LayoutDashboard } from "lucide-react";

function itemActive(pathname, to, siblingTos = []) {
  if (to === "/") return pathname === "/";
  if (pathname === to) return true;
  if (pathname.startsWith(`${to}/`)) {
    const longerSibling = siblingTos.some(
      (other) => other !== to && other.startsWith(`${to}/`) && (pathname === other || pathname.startsWith(`${other}/`)),
    );
    if (longerSibling) return false;
    return true;
  }
  return false;
}

function groupActive(pathname, items) {
  const siblingTos = items.map((n) => n.to);
  return items.some((item) => itemActive(pathname, item.to, siblingTos));
}

/**
 * Phone-friendly module browser: pick a top-level tab, then its sub-tabs.
 * Parent sheet owns vertical scrolling — keep this content naturally tall.
 */
export default function MobileModuleMenu({ groups = [], onNavigate }) {
  const { pathname } = useLocation();
  const activeGroupId = useMemo(() => {
    const hit = groups.find((g) => groupActive(pathname, g.items || []));
    return hit?.id || null;
  }, [groups, pathname]);

  const [drillGroupId, setDrillGroupId] = useState(null);

  useEffect(() => {
    setDrillGroupId(null);
  }, [pathname]);

  const drillGroup = groups.find((g) => g.id === drillGroupId) || null;

  if (drillGroup) {
    const siblingTos = drillGroup.items.map((n) => n.to);
    return (
      <div className="pb-8" data-testid="mobile-module-menu-sub">
        <button
          type="button"
          onClick={() => setDrillGroupId(null)}
          className="mx-2 mb-2 flex items-center gap-2 rounded-xl px-3 py-3 min-h-12 text-sm text-bansal-ivory/90 active:bg-white/10 touch-manipulation"
          data-testid="mobile-module-back"
        >
          <ChevronLeft className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          <span className="font-medium">All modules</span>
        </button>
        <div className="px-4 pb-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-bansal-ivory/45 font-semibold">
            {drillGroup.label}
          </p>
          <p className="text-xs text-bansal-ivory/55 mt-1">
            {drillGroup.items.length} page{drillGroup.items.length === 1 ? "" : "s"}
          </p>
        </div>
        <ul className="px-2 space-y-1">
          {drillGroup.items.map((item) => {
            const active = itemActive(pathname, item.to, siblingTos);
            const Icon = item.icon || LayoutDashboard;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={siblingTos.some((other) => other !== item.to && other.startsWith(`${item.to}/`))}
                  onClick={onNavigate}
                  data-testid={`mobile-module-sub-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className={`flex items-center gap-3 rounded-xl px-3 py-3.5 min-h-14 touch-manipulation transition-colors ${
                    active
                      ? "bg-bansal-sage/25 text-bansal-gold font-medium"
                      : "text-bansal-ivory/90 active:bg-white/10"
                  }`}
                >
                  <Icon className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
                  <span className="flex-1 truncate text-sm leading-snug">{item.label}</span>
                  {active ? (
                    <span className="text-[10px] uppercase tracking-wide text-bansal-gold/90">Open</span>
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 opacity-35" aria-hidden />
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="pb-8" data-testid="mobile-module-menu">
      <div className="px-4 pb-2">
        <p className="text-xs text-bansal-ivory/55">
          Tap a module, then choose its page. Current:{" "}
          <span className="text-bansal-gold/90">
            {groups.find((g) => g.id === activeGroupId)?.label || "Home"}
          </span>
        </p>
      </div>
      <ul className="px-2 space-y-1">
        {groups.map((group) => {
          const active = group.id === activeGroupId;
          const count = group.items?.length || 0;
          const firstIcon = group.items?.[0]?.icon || LayoutDashboard;
          const Icon = firstIcon;
          const isSingle = count === 1;

          if (isSingle) {
            const item = group.items[0];
            const ItemIcon = item.icon || LayoutDashboard;
            const on = itemActive(pathname, item.to, [item.to]);
            return (
              <li key={group.id}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onNavigate}
                  className={`flex items-center gap-3 rounded-xl px-3 py-3.5 min-h-14 touch-manipulation transition-colors ${
                    on
                      ? "bg-bansal-sage/25 text-bansal-gold font-medium"
                      : "text-bansal-ivory/90 active:bg-white/10"
                  }`}
                >
                  <ItemIcon className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bansal-ivory/40">{group.label}</div>
                    <div className="text-sm truncate leading-snug mt-0.5">{item.label}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 opacity-35" aria-hidden />
                </NavLink>
              </li>
            );
          }

          return (
            <li key={group.id}>
              <button
                type="button"
                onClick={() => setDrillGroupId(group.id)}
                data-testid={`mobile-module-${group.id}`}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-3.5 min-h-14 text-left touch-manipulation transition-colors ${
                  active
                    ? "bg-white/[0.08] text-bansal-gold"
                    : "text-bansal-ivory/90 active:bg-white/10"
                }`}
              >
                <Icon className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{group.label}</div>
                  <div className="text-[11px] text-bansal-ivory/45 mt-0.5">
                    {count} pages{active ? " · current" : ""}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 opacity-45" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
      <p className="px-4 pt-3 text-[10px] text-bansal-ivory/35 text-center">
        Scroll for Lab, Inventory, Production, Finance, and Admin
      </p>
    </div>
  );
}
